import { useEffect, useState } from "react";
import { MessageSquareHeart } from "lucide-react";
import posthog from "@/lib/posthog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/enhanced-button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { submitFeedback, type FeedbackType } from "@/services/feedback.service";

const AREAS = ["Home", "AI Agents", "Finance", "Settings", "Other"];

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
  screen: string;
  defaultArea: string;
}

export function FeedbackModal({ open, onClose, screen, defaultArea }: FeedbackModalProps) {
  const [title, setTitle] = useState("");
  const [area, setArea] = useState(defaultArea);
  const [type, setType] = useState<FeedbackType>("bug");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (open) {
      setArea(defaultArea);
      posthog.capture("feedback_opened", { screen });
    }
  }, [open, defaultArea, screen]);

  const reset = () => {
    setTitle("");
    setType("bug");
    setDescription("");
    setSubmitError(null);
    setSubmitted(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await submitFeedback({ title: title.trim(), area, type, description: description.trim(), screen });
      posthog.capture("feedback_submitted", { area, type, screen, title: title.trim(), description: description.trim() });
      setSubmitted(true);
    } catch {
      setSubmitError("Failed to send feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquareHeart className="h-4 w-4 text-purple-600" />
            Send Feedback
          </DialogTitle>
          <DialogDescription>Report a bug or share a suggestion — it helps us improve BlinkBooks.</DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">Thanks — your feedback has been sent.</p>
            <div className="flex justify-end">
              <Button onClick={handleClose}>Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <div>
              <Label htmlFor="feedback-title" className="mb-2 block">Title</Label>
              <Input
                id="feedback-title"
                placeholder="Short summary"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={120}
              />
            </div>

            <div>
              <Label className="mb-2 block">Area</Label>
              <Select value={area} onValueChange={setArea}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AREAS.map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="mb-2 block">Type</Label>
              <RadioGroup value={type} onValueChange={(v) => setType(v as FeedbackType)} className="grid-flow-col justify-start gap-6">
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="bug" id="feedback-type-bug" />
                  <label htmlFor="feedback-type-bug" className="text-sm cursor-pointer select-none">Bug</label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="suggestion" id="feedback-type-suggestion" />
                  <label htmlFor="feedback-type-suggestion" className="text-sm cursor-pointer select-none">Suggestion</label>
                </div>
              </RadioGroup>
            </div>

            <div>
              <Label htmlFor="feedback-description" className="mb-2 block">Description</Label>
              <Textarea
                id="feedback-description"
                placeholder="What happened, or what would you like to see? Include steps to reproduce if it's a bug."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="min-h-[100px] resize-none"
              />
            </div>

            {submitError && <p className="text-sm text-destructive">{submitError}</p>}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!title.trim() || !description.trim() || isSubmitting}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {isSubmitting ? "Sending…" : "Send Feedback"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
